/**
 * MILLCOT grout coverage calculator
 * Vanilla JS, no dependencies. One set of delegated listeners on document, so
 * any number of calculators work independently and theme-editor section
 * reloads need no re-initialisation.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.MillcotGroutCalc) { return; }

  /* ------------------------------------------------------------------
   * PRODUCT CONFIGURATION
   * Add other grouts here ONLY once their own factor has been verified.
   * Never reuse one product's factor for another product.
   * Shopify product handles are mapped to these keys in
   * snippets/millcot-grout-calculator.liquid.
   *
   * packWeightKg : fixed pack weight (single-size products)
   * packOption / packs : Shopify option that carries the pack size, and the
   *   option values the calculator understands. Customers are only ever
   *   offered sizes that exist as real variants of the Shopify product.
   * packFrom: 'suffix' : the pack size is the trailing "2kg"/"5kg" of ONE combined
   *   option value such as "Silver Grey 2kg" (see parseCombined); the text before it is the colour.
   * allowance : multiplier applied once to the requirement (omit for none).
   * depthName : what the "D" measurement is called for this product.
   * ------------------------------------------------------------------ */
  var PRODUCTS = {
    'fugabella-color-3kg': {
      enabled: true,
      name: 'Kerakoll Fugabella Color',
      method: 'joint-volume-factor',
      factor: 1.9,            // coverage factor ("density" in Kerakoll's calculator config). NOT the 1.7 mixture density.
      packWeightKg: 3,        // 3 kg bags only
      unit: ['bag', 'bags'],
      depthName: 'tile thickness',
      jointMinMm: 1,
      jointMaxMm: 20,
      wastageIncluded: true,  // manufacturer's coverage already allows for normal site wastage
      source: 'https://calcolatore.kerakoll.com/?product=T00421&lang=en&market=gb',
      sourceNote: 'Kerakoll calculator, product T00421 "Fugabella Color UK", factor stored as "density": 1.9',
      verified: '2026-10-12',
      verifiedBy: 'MILLCOT (inspection of Kerakoll calculator configuration)'
    },
    'ardex-flex-fl': {
      enabled: true,
      name: 'ARDEX-FLEX FL',
      method: 'joint-volume-factor',
      factor: 1.75,
      packOption: 'Size',
      packs: { '2.5kg': 2.5, '5kg': 5, '10kg': 10 },
      unit: ['bag', 'bags'],
      depthName: 'joint depth',
      jointMinMm: 3,
      jointMaxMm: 15,
      wastageIncluded: false, // ARDEX: calculator excludes site conditions and wastage
      source: 'https://ardex.co.uk/product/ardex-flex-fl/',
      sourceNote: 'ARDEX website calculator HTML, product option value "1.75,10" (coverage factor, manufacturer default pack). ' +
        'HTML configuration inspected only; ARDEX\'s calculation script and display rounding were not verified. ' +
        'MILLCOT calculates with the pack size actually selected, not the 10 kg default.',
      verified: '2026-10-13',
      verifiedBy: 'MILLCOT (inspection of ARDEX calculator HTML)'
    },
    'ardex-flex-fs': {
      enabled: true,
      name: 'ARDEX-FLEX FS',
      method: 'joint-volume-factor',
      factor: 1.5,
      packOption: 'Size',
      packs: { '2.5kg': 2.5, '5kg': 5, '10kg': 10 },
      unit: ['bag', 'bags'],
      depthName: 'joint depth',
      jointMinMm: 1,
      jointMaxMm: 4,
      wastageIncluded: false,
      source: 'https://ardex.co.uk/product/ardex-flex-fs/',
      sourceNote: 'ARDEX website calculator HTML, product option value "1.5,10". HTML configuration inspected only; ' +
        'calculation script and display rounding not verified. Joint range 1-4 mm matches the calculator inputs.',
      verified: '2026-10-13',
      verifiedBy: 'MILLCOT (inspection of ARDEX calculator HTML)'
    },
    'ardex-eg-18-plus': {
      enabled: true,
      name: 'ARDEX EG 18 PLUS',
      method: 'joint-volume-factor',
      factor: 1.7,            // DECISION: website calculator value. The technical datasheet gives 1.5; MILLCOT chose to follow the website calculator. Do not change to 1.5 without MILLCOT sign-off.
      packWeightKg: 4,        // complete 4 kg kit; never split into components
      unit: ['kit', 'kits'],
      depthName: 'joint depth',
      jointMinMm: 2,
      jointMaxMm: 12,
      wastageIncluded: false,
      source: 'https://ardex.co.uk/product/ardex-eg-18-plus/',
      sourceNote: 'ARDEX website calculator HTML, product option value "1.7,4". The technical datasheet states 1.5; ' +
        'MILLCOT explicitly decided to use the website calculator figure of 1.7. HTML configuration inspected only; ' +
        'calculation script and display rounding not verified.',
      verified: '2026-10-13',
      verifiedBy: 'MILLCOT (inspection of ARDEX calculator HTML)'
    }
  };

  PRODUCTS['mapei-ultracolor-plus'] = {
    enabled: true,
    name: 'Mapei Ultracolor Plus',
    method: 'joint-volume-factor',
    factor: 1.6,              // "GoutKCostant" (Mapei's own spelling) in the product-data endpoint
    allowance: 1.1,           // Mapei's calculator multiplies the requirement by 1.1 (10% allowance). Applied exactly once, in calculate().
    packOption: 'Size & Colour',
    packFrom: 'suffix',       // one combined option, e.g. "White 2kg", "Artic Grey 5kg"
    packs: { '2kg': 2, '5kg': 5 }, // Mapei also lists 22 kg; it is not a MILLCOT variant, so it can never be offered
    unit: ['bag', 'bags'],
    depthName: 'tile thickness',
    jointMinMm: 2,            // GroutJointWidthMin
    jointMaxMm: 20,           // GroutJointWidthMax
    wastageIncluded: true,    // via the 10% allowance above
    source: 'https://www.mapei.com/gb/en/products-and-solutions/products/detail/ultracolor-plus',
    sourceScript: 'https://cdnmedia.mapei.com/Mvc/Scripts/Common/function-common-base-grouts-sealants-calculators.js?v=2026081102',
    sourceData: 'https://www.mapei.com/restapi/calculators/base-grouts-sealants/product-detail?SiteId=121bdd71-79c5-62e4-9128-ff01007028e9&ProductId=f37a3e72-79c5-62e4-9128-ff01007028e9&CultureName=en',
    sourceNote: 'UK ULTRACOLOR PLUS (not FA, Max or other regional formulations). GoutKCostant 1.6, joint width 2-20 mm, x1.1 allowance in the calculator script. ' +
      'ROUNDING DIFFERENCE (deliberate): Mapei rounds the kg with toFixed(1) and counts packs from that rounded figure. ' +
      'MILLCOT shows the kg rounded UP to 0.1 and counts bags from the full unrounded requirement including the allowance, ' +
      'so displayed figures are not guaranteed to match Mapei\'s website. Nothing is fetched from Mapei at run time.',
    verified: '2026-10-13',
    verifiedBy: 'MILLCOT (inspection of Mapei page HTML, calculator script and product-data endpoint)'
  };

  /* Calculation methods: (config, {L, W, D, J} in mm) -> kg per m2
     D is tile thickness or joint depth, depending on the product's depthName. */
  var METHODS = {
    'joint-volume-factor': function (cfg, i) {
      return ((i.L + i.W) / (i.L * i.W)) * i.D * i.J * cfg.factor;
    }
  };

  var EPS = 1e-9; // absorbs floating-point noise at exact boundaries only

  function getConfig(key) {
    var cfg = PRODUCTS[key];
    return cfg && cfg.enabled && METHODS[cfg.method] ? cfg : null;
  }

  function normPack(s) { return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ''); }

  /* Combined option value such as "Silver Grey 2kg", "White 5 KG" or "Moon White - 5kg"
     -> { colour: 'Silver Grey', pack: '2kg' }. pack is null when there is no kg suffix. */
  function parseCombined(value) {
    var s = String(value == null ? '' : value).trim();
    var m = /^(.*?)[\s\-\/,]*(\d+(?:\.\d+)?)\s*kg$/i.exec(s);
    if (!m) { return { colour: s, pack: null }; }
    return { colour: m[1].trim() || s, pack: String(Number(m[2])) + 'kg' };
  }

  /* Pack weight in kg for a Shopify pack option value, or null if unknown. */
  function packWeightFor(cfg, optionValue) {
    if (cfg.packWeightKg) { return cfg.packWeightKg; }
    var w = cfg.packs ? cfg.packs[normPack(optionValue)] : null;
    return w || null;
  }

  function calculate(cfg, i, packWeightKg) {
    var pack = packWeightKg || cfg.packWeightKg;
    var base = METHODS[cfg.method](cfg, i);
    var consumption = base * (cfg.allowance || 1); // any allowance is applied here, once, and nowhere else
    var kg = consumption * i.A;
    if (!pack || !isFinite(consumption) || !isFinite(kg) || consumption <= 0 || kg <= 0) { return null; }
    var bags = Math.max(1, Math.ceil(kg / pack - EPS));
    return {
      consumptionBase: base,
      consumption: consumption,
      kg: kg,
      kgDisplay: Math.ceil(kg * 10 - EPS) / 10, // MILLCOT display convention: rounded UP to 0.1 kg
      pack: pack,
      bags: bags,
      supplied: bags * pack,
      coveragePerPack: pack / consumption
    };
  }

  /* ---------- validation ---------- */

  function parseWholeMm(raw, what, min, max, productName) {
    var s = String(raw == null ? '' : raw).trim();
    if (s === '') { return { error: 'Enter the ' + what + ' in mm.' }; }
    if (/^-/.test(s)) { return { error: 'The ' + what + ' must be greater than 0.' }; }
    if (/^\+?\d*[.,]\d*$/.test(s) && /\d/.test(s)) {
      return { error: 'Enter a whole number of millimetres. Decimals such as ' + s + ' are not accepted.' };
    }
    if (!/^\d+$/.test(s)) { return { error: 'Enter a whole number, for example ' + (min != null ? '3' : '600') + '.' }; }
    var n = Number(s);
    if (!isFinite(n)) { return { error: 'Enter a valid ' + what + '.' }; }
    if (n <= 0) { return { error: 'The ' + what + ' must be greater than 0.' }; }
    if (min != null && (n < min || n > max)) {
      return { error: 'Enter a joint width from ' + min + ' to ' + max + ' mm' + (productName ? ' for ' + productName : '') + '.' };
    }
    return { value: n };
  }

  function parseArea(raw) {
    var s = String(raw == null ? '' : raw).trim();
    if (s === '') { return { error: 'Enter the area to grout in m².' }; }
    if (/^-/.test(s)) { return { error: 'The area must be greater than 0.' }; }
    if (/^\d+,\d+$/.test(s)) { return { error: 'Use a decimal point, for example 12.5.' }; }
    if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) { return { error: 'Enter a number, for example 12.5.' }; }
    var n = Number(s);
    if (!isFinite(n)) { return { error: 'Enter a valid area.' }; }
    if (n <= 0) { return { error: 'The area must be greater than 0.' }; }
    return { value: n };
  }

  function parseJoint(cfg, raw) {
    return parseWholeMm(raw, 'joint width', cfg.jointMinMm, cfg.jointMaxMm, cfg.name);
  }

  function validate(cfg, raw) {
    var r = {
      length: parseWholeMm(raw.length, 'tile length'),
      width: parseWholeMm(raw.width, 'tile width'),
      thickness: parseWholeMm(raw.thickness, cfg.depthName || 'tile thickness'),
      joint: parseJoint(cfg, raw.joint),
      area: parseArea(raw.area)
    };
    var errors = {}, ok = true, k;
    for (k in r) { if (r[k].error) { errors[k] = r[k].error; ok = false; } }
    return {
      ok: ok,
      errors: errors,
      inputs: ok ? { L: r.length.value, W: r.width.value, D: r.thickness.value, J: r.joint.value, A: r.area.value } : null
    };
  }

  /* ---------- formatting ---------- */

  function trimZeros(s) { return s.indexOf('.') === -1 ? s : s.replace(/0+$/, '').replace(/\.$/, ''); }
  function fmtConsumption(x) { return x >= 0.001 ? trimZeros(x.toFixed(3)) : x.toPrecision(2); }
  function fmtCoverage(x) { return x.toFixed(1); }
  function fmtPack(w) { return String(w) + ' kg'; }
  function unitWord(cfg, n) { var u = cfg.unit || ['bag', 'bags']; return n === 1 ? u[0] : u[1]; }

  var api = {
    products: PRODUCTS, getConfig: getConfig, calculate: calculate, validate: validate, packWeightFor: packWeightFor, parseCombined: parseCombined,
    fmtConsumption: fmtConsumption, fmtCoverage: fmtCoverage
  };

  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (typeof window === 'undefined' || typeof document === 'undefined') { return; }
  window.MillcotGroutCalc = api;

  /* ------------------------------------------------------------------
   * UI (all state lives in the DOM of each [data-mgc] root)
   * ------------------------------------------------------------------ */
  var FIELDS = ['length', 'width', 'thickness', 'joint', 'area'];

  function q(root, sel) { return root.querySelector(sel); }
  function field(root, name) { return q(root, '[data-mgc-field="' + name + '"]'); }
  function setText(root, name, text) { var el = q(root, '[data-mgc-out="' + name + '"]'); if (el) { el.textContent = text; } }
  function configOf(root) { return getConfig(root.getAttribute('data-mgc-config')); }
  function isProductContext(root) { return root.getAttribute('data-mgc-context') === 'product'; }

  function setError(root, name, msg) {
    var input = field(root, name), err = q(root, '[data-mgc-error="' + name + '"]');
    if (!err) { return; }
    err.textContent = msg || '';
    err.hidden = !msg;
    if (input) {
      if (msg) { input.setAttribute('aria-invalid', 'true'); } else { input.removeAttribute('aria-invalid'); }
    }
  }

  function clearResults(root) {
    var res = q(root, '[data-mgc-results]');
    if (res) { res.hidden = true; }
    root.removeAttribute('data-mgc-bags');
    root.removeAttribute('data-mgc-pack-weight');
    var status = q(root, '[data-mgc-status]');
    if (status) { status.textContent = ''; }
    setBuyMessage(root, '');
  }

  function variants(root) {
    if (root.__mgcVariants) { return root.__mgcVariants; }
    var el = q(root, '[data-mgc-variants]'), list = [];
    try { list = el ? JSON.parse(el.textContent) : []; } catch (e) { list = []; }
    var cfg = configOf(root);
    if (cfg && cfg.packFrom === 'suffix') {
      list.forEach(function (v) { var p = parseCombined(v.raw); v.pack = p.pack || ''; v.title = p.colour; });
    }
    root.__mgcVariants = list;
    return list;
  }

  function findVariant(root, id) {
    var list = variants(root), i;
    for (i = 0; i < list.length; i++) { if (String(list[i].id) === String(id)) { return list[i]; } }
    return null;
  }

  /* Currently selected variant (colour, and size where the product has sizes).
     Product page: read the theme's own product form (never changes it).
     Calculator page: the customer's explicit choice. */
  function selectedVariant(root) {
    var select = q(root, '[data-mgc-colour]');
    if (select) { return select.value ? findVariant(root, select.value) : null; }
    if (!isProductContext(root)) { return null; }
    var pid = root.getAttribute('data-mgc-product-id');
    var formPrefix = 'product-form-main-' + pid + '-';
    var input = document.querySelector('form[id^="' + formPrefix + '"] [name="id"]') ||
                document.querySelector('[name="id"][form^="' + formPrefix + '"]');
    // An empty id means the customer has chosen a size/colour combination that does not exist:
    // never fall back to an earlier selection in that case.
    if (input) { return input.value ? findVariant(root, input.value) : null; }
    var id = root.getAttribute('data-mgc-last-variant');
    return id ? findVariant(root, id) : null;
  }

  /* Pack size to calculate with: { weight } or { error }. Never guessed. */
  function resolvePack(root, cfg) {
    if (cfg.packWeightKg) { return { weight: cfg.packWeightKg }; }
    var value;
    if (isProductContext(root)) {
      var v = selectedVariant(root);
      if (!v) { return { error: 'That size and colour combination is not available. Choose another using the product options above, then calculate again.' }; }
      value = v.pack;
    } else {
      var select = field(root, 'pack');
      value = select ? select.value : '';
      if (!value) { return { error: 'Choose a pack size.' }; }
    }
    var w = packWeightFor(cfg, value);
    return w ? { weight: w } : { error: 'The calculator does not support ' + (value ? 'the ' + value + ' pack size' : 'this pack size') + ' yet.' };
  }

  /* Calculator page: only offer colours that exist in the chosen pack size. */
  function syncColours(root) {
    var colour = q(root, '[data-mgc-colour]'), pack = field(root, 'pack');
    if (!colour || !pack) { return; }
    var want = normPack(pack.value);
    Array.prototype.forEach.call(colour.options, function (o) {
      if (o.hasAttribute('data-raw') && !o.hasAttribute('data-pack')) { // combined "Colour 2kg" value: split once
        var p = parseCombined(o.getAttribute('data-raw'));
        o.setAttribute('data-pack', p.pack || 'unsupported');
        o.textContent = p.colour + (o.hasAttribute('data-unavailable') ? ' (unavailable)' : '');
      }
      if (!o.hasAttribute('data-pack')) { return; }
      var mismatch = normPack(o.getAttribute('data-pack')) !== want;
      o.hidden = mismatch;
      o.disabled = mismatch || o.hasAttribute('data-unavailable');
    });
    var current = colour.options[colour.selectedIndex];
    if (current && current.disabled) { colour.value = ''; }
  }

  function setBuyMessage(root, msg, isError) {
    var el = q(root, '[data-mgc-buy-msg]');
    if (!el) { return; }
    el.textContent = msg || '';
    el.hidden = !msg;
    el.classList.toggle('mgc__buy-msg--error', !!isError);
  }

  function unavailableText(cfg, v) {
    return 'Colour ' + v.title + ' is currently unavailable' + (cfg.packs ? ' in this size' : '') + '.';
  }

  function packText(root, cfg, n) {
    return n + ' × ' + fmtPack(Number(root.getAttribute('data-mgc-pack-weight'))) + ' ' + unitWord(cfg, n);
  }

  function refreshBuy(root) {
    var btn = q(root, '[data-mgc-add]'), cfg = configOf(root);
    if (!btn || !cfg) { return; }
    var bags = Number(root.getAttribute('data-mgc-bags')) || 0;
    var v = selectedVariant(root);
    btn.textContent = 'Add ' + packText(root, cfg, bags) + ' to basket';
    var colour = q(root, '[data-mgc-colour-label]');
    if (colour) {
      colour.textContent = v ? 'Colour selected: ' + v.title : '';
      colour.hidden = !v;
    }
    if (isProductContext(root) && !v) {
      btn.hidden = true; // cannot read the selected colour reliably: fall back to the product link
    } else {
      btn.hidden = false;
      btn.disabled = !!(v && !v.available);
      if (v && !v.available) { setBuyMessage(root, unavailableText(cfg, v), true); }
    }
  }

  function onCalculate(root, quiet) {
    var cfg = configOf(root);
    if (!cfg) { return; }
    var raw = {};
    FIELDS.forEach(function (n) { var f = field(root, n); raw[n] = f ? f.value : ''; });
    var v = validate(cfg, raw), pack = resolvePack(root, cfg), firstBad = null;
    setError(root, 'pack', pack.error);
    if (pack.error) { firstBad = field(root, 'pack'); }
    FIELDS.forEach(function (n) {
      setError(root, n, v.errors[n]);
      if (v.errors[n] && !firstBad) { firstBad = field(root, n); }
    });
    clearResults(root);
    if (!v.ok || pack.error) { if (firstBad && !quiet) { firstBad.focus(); } return; }

    var r = calculate(cfg, v.inputs, pack.weight);
    if (!r) { setError(root, 'area', 'These values cannot be calculated. Please check them.'); return; }

    root.setAttribute('data-mgc-bags', String(r.bags));
    root.setAttribute('data-mgc-pack-weight', String(r.pack));
    var kgText = r.kgDisplay.toFixed(1) + ' kg';
    var bagText = packText(root, cfg, r.bags);
    setText(root, 'kg', kgText);
    setText(root, 'bags', bagText);
    setText(root, 'supplied', trimZeros(r.supplied.toFixed(1)) + ' kg');
    setText(root, 'consumption', fmtConsumption(r.consumption) + ' kg/m²');
    setText(root, 'packlabel', fmtPack(r.pack) + ' ' + unitWord(cfg, 1));
    setText(root, 'coverage', 'approx. ' + fmtCoverage(r.coveragePerPack) + ' m²');
    q(root, '[data-mgc-results]').hidden = false;
    syncColours(root);
    refreshBuy(root);
    var status = q(root, '[data-mgc-status]');
    if (status) {
      status.textContent = 'You need approximately ' + kgText + '. Buy ' + bagText + '. Total supplied: ' + trimZeros(r.supplied.toFixed(1)) + ' kg.';
    }
  }

  function onReset(root) {
    FIELDS.concat(['pack']).forEach(function (n) { var f = field(root, n); if (f) { f.value = ''; } setError(root, n, ''); });
    var select = q(root, '[data-mgc-colour]');
    if (select) { select.value = ''; }
    clearResults(root);
    var first = field(root, 'pack') || field(root, 'length');
    if (first) { first.focus(); }
  }

  function onAdd(root, btn) {
    var cfg = configOf(root);
    var bags = Number(root.getAttribute('data-mgc-bags')) || 0;
    if (!cfg || !bags || btn.disabled) { return; }
    var v = selectedVariant(root), select = q(root, '[data-mgc-colour]');
    if (!v) {
      setBuyMessage(root, select ? 'Choose a grout colour first.' : 'Please choose your colour and quantity using the product options.', true);
      if (select) { select.focus(); }
      return;
    }
    // The quantity was calculated for one pack size: never add it against a different size.
    if (packWeightFor(cfg, v.pack) !== Number(root.getAttribute('data-mgc-pack-weight'))) {
      onCalculate(root, true);
      setBuyMessage(root, 'The pack size changed, so the result has been updated. Please check it and add again.', true);
      return;
    }
    if (!v.available) { setBuyMessage(root, unavailableText(cfg, v), true); return; }

    var url = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';
    var added = packText(root, cfg, bags);
    btn.disabled = true; // blocks duplicate submissions while the request is pending
    setBuyMessage(root, 'Adding to basket…');
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(v.id), quantity: bags }] })
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) { throw new Error(data.description || data.message || ''); }
        return data;
      });
    }).then(function () {
      setBuyMessage(root, 'Added ' + added + ', colour ' + v.title + ', to your basket.');
      // Warehouse theme hook: updates the cart count and opens the mini-cart (it only opens when productAdded is true)
      document.documentElement.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true, detail: { variant: v, quantity: bags, productAdded: true }
      }));
    }).catch(function (err) {
      // Shopify's own message is shown as it is (sold out, or only part of the quantity could be added)
      setBuyMessage(root, err && err.message ? err.message :
        'Sorry, that could not be added to your basket. You can order from the product page instead.', true);
      // A stock-limited request can still add part of the quantity, so always bring the cart count up to date
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
    }).then(function () { btn.disabled = false; });
  }

  /* Dedicated multi-product page: show the chosen product's calculator and carry the
     measurements across. A joint width outside the new product's range is flagged at once. */
  function onSwitch(sw) {
    var group = sw.closest('[data-mgc-group]');
    if (!group) { return; }
    var from = null, to = null;
    Array.prototype.forEach.call(group.querySelectorAll('[data-mgc-panel]'), function (p) {
      if (!p.hidden) { from = p; }
      if (p.getAttribute('data-mgc-panel') === sw.value) { to = p; }
    });
    if (!to || from === to) { return; }
    var fr = from ? q(from, '[data-mgc]') : null, tr = q(to, '[data-mgc]');
    if (from) { from.hidden = true; }
    to.hidden = false;
    if (!tr) { return; }
    FIELDS.forEach(function (n) {
      var a = fr ? field(fr, n) : null, b = field(tr, n);
      if (a && b) { b.value = a.value; }
      setError(tr, n, '');
    });
    setError(tr, 'pack', '');
    if (fr) { clearResults(fr); }
    clearResults(tr);
    var cfg = configOf(tr), joint = field(tr, 'joint');
    if (cfg && joint && joint.value.trim() !== '') {
      var res = parseJoint(cfg, joint.value);
      if (res.error) { setError(tr, 'joint', res.error); }
    }
  }

  function rootOf(el) { return el && el.closest ? el.closest('[data-mgc]') : null; }

  document.addEventListener('submit', function (e) {
    var root = rootOf(e.target);
    if (!root) { return; }
    e.preventDefault();
    e.stopPropagation();
    onCalculate(root);
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-mgc-reset],[data-mgc-add]') : null;
    var root = rootOf(t);
    if (!root) { return; }
    e.preventDefault();
    if (t.hasAttribute('data-mgc-reset')) { onReset(root); } else { onAdd(root, t); }
  });

  // "Calculate how much grout you need" link beside Add to cart. Handled here (capture phase)
  // because the theme cancels in-page anchor clicks inside the product form.
  document.addEventListener('click', function (e) {
    var jump = e.target.closest ? e.target.closest('[data-mgc-jump]') : null;
    var target = jump ? document.getElementById('millcot-grout-calculator') : null;
    if (!target) { return; }
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    var first = target.querySelector('[data-mgc-field]');
    if (first) { first.focus({ preventScroll: true }); }
  }, true);

  document.addEventListener('input', function (e) {
    var root = rootOf(e.target);
    if (!root || !e.target.hasAttribute('data-mgc-field')) { return; }
    var name = e.target.getAttribute('data-mgc-field');
    setError(root, name, '');
    clearResults(root); // an old result must never sit beside changed inputs
    if (name === 'pack') { syncColours(root); }
  });

  document.addEventListener('change', function (e) {
    if (e.target.hasAttribute && e.target.hasAttribute('data-mgc-switch')) { onSwitch(e.target); return; }
    var root = rootOf(e.target);
    if (!root || !e.target.hasAttribute('data-mgc-colour')) { return; }
    setBuyMessage(root, '');
    if (root.getAttribute('data-mgc-bags')) { refreshBuy(root); }
  });

  // Warehouse fires this when the customer changes colour or size on the product page
  document.addEventListener('variant:changed', function (e) {
    var variant = e.detail && e.detail.variant;
    Array.prototype.forEach.call(document.querySelectorAll('[data-mgc][data-mgc-context="product"]'), function (root) {
      if (variant && findVariant(root, variant.id)) { root.setAttribute('data-mgc-last-variant', variant.id); } else { root.removeAttribute('data-mgc-last-variant'); }
      setBuyMessage(root, '');
      // A shown result is recalculated for the newly selected size and colour
      if (root.getAttribute('data-mgc-bags')) { onCalculate(root, true); }
    });
  });
})();
